import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, LogIn, Lock, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
    } catch (err) {
      // Always show success to prevent email enumeration
      setForgotSuccess(true);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image - Abstract Eye */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/510e7d04-1afd-4310-a1da-be47f5ecb065/images/a49a34ad75224d3f2c914559238d9d3b5c31ed132bfd66550adbabe8b7e01e86.jpeg')`,
        }}
      >
        {/* Gradient overlay for better readability on left side */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#051520]/95 via-[#051520]/70 to-transparent"></div>
        
        {/* Subtle blur on the left portion */}
        <div className="absolute inset-y-0 left-0 w-1/2 backdrop-blur-[2px]"></div>
      </div>

      {/* Main Content - Split Layout */}
      <div className="relative min-h-screen flex">
        {/* Left Side - Login Form */}
        <div className="w-full lg:w-[480px] min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-12">
          {/* Glassmorphism Card */}
          <div className="w-full max-w-sm">
            <div className="relative backdrop-blur-2xl bg-white/[0.15] rounded-3xl border border-white/[0.12] shadow-2xl overflow-hidden">
              {/* Teal/Cyan accent glow at top */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent"></div>
              
              {/* Inner glow effect matching eye colors */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/[0.08] via-transparent to-amber-500/[0.05] pointer-events-none"></div>
              
              {/* Edge highlight */}
              <div className="absolute inset-[1px] rounded-3xl border border-white/[0.08] pointer-events-none"></div>
              
              {/* Content */}
              <div className="relative p-8">
                {/* Logo with light background box */}
                <div className="text-center mb-6">
                  <div className="inline-block bg-slate-100/90 backdrop-blur-sm rounded-2xl p-4 shadow-inner">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_510e7d04-1afd-4310-a1da-be47f5ecb065/artifacts/1ciuvk9j_logo%20cristian%20acevedo.png" 
                      alt="Dr. Cristian R. Acevedo"
                      className="h-40 mx-auto"
                    />
                  </div>
                </div>

                {/* Welcome text */}
                <div className="text-center mb-6">
                  <h1 className="text-xl font-semibold text-white/90 font-display">Bienvenido</h1>
                  <p className="text-white/40 text-sm mt-1">Ingresa tus credenciales</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 backdrop-blur-sm">
                      <p className="text-red-300 text-sm text-center">{error}</p>
                    </div>
                  )}

                  {/* Email Field */}
                  <div className="space-y-1.5">
                    <label className="text-white/50 text-xs font-medium tracking-wide pl-1">
                      Correo Electrónico
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-amber-500/10 rounded-xl blur opacity-0 group-focus-within:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative flex items-center">
                        <Mail className="absolute left-3 w-4 h-4 text-white/25 group-focus-within:text-cyan-400 transition-colors" />
                        <input
                          type="email"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          required
                          placeholder="correo@ejemplo.com"
                          autoComplete="email"
                          className="w-full h-11 pl-10 pr-4 bg-white/[0.05] border border-white/[0.08] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-cyan-400/40 focus:bg-white/[0.08] transition-all"
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="space-y-1.5">
                    <label className="text-white/50 text-xs font-medium tracking-wide pl-1">
                      Contraseña
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-amber-500/10 rounded-xl blur opacity-0 group-focus-within:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative flex items-center">
                        <Lock className="absolute left-3 w-4 h-4 text-white/25 group-focus-within:text-cyan-400 transition-colors" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          placeholder="••••••••"
                          className="w-full h-11 pl-10 pr-10 bg-white/[0.05] border border-white/[0.08] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-cyan-400/40 focus:bg-white/[0.08] transition-all"
                          data-testid="login-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 text-white/25 hover:text-white/50 transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="relative w-full h-11 rounded-xl font-medium text-white text-sm overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="login-submit-button"
                    >
                      {/* Button gradient background - matching eye colors */}
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-teal-500 to-cyan-600 transition-all group-hover:from-cyan-400 group-hover:via-teal-400 group-hover:to-cyan-500"></div>
                      
                      {/* Button glow */}
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-teal-500 blur-xl opacity-40 group-hover:opacity-60 transition-opacity"></div>
                      
                      {/* Button content */}
                      <div className="relative flex items-center justify-center gap-2">
                        {loading ? (
                          <>
                            <div className="flex gap-1">
                              <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                              <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                              <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                            </div>
                            <span>Ingresando...</span>
                          </>
                        ) : (
                          <>
                            <LogIn className="w-4 h-4" />
                            <span>Iniciar Sesión</span>
                          </>
                        )}
                      </div>
                    </button>

                    {/* Forgot Password Link */}
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="w-full mt-3 text-xs text-cyan-400/60 hover:text-cyan-300 transition-colors"
                      data-testid="forgot-password-link"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                </form>

                {/* Footer */}
                <div className="mt-6 text-center">
                  <p className="text-white/20 text-[10px] tracking-wider uppercase">
                    Sistema de Gestión Médica
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Empty space for eye image visibility */}
        <div className="hidden lg:flex flex-1">
          {/* Eye image visible on right side */}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="relative w-full max-w-md backdrop-blur-2xl bg-[#051520]/90 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
            {/* Modal accent */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent"></div>
            
            <div className="relative p-8">
              {forgotSuccess ? (
                <div className="text-center">
                  <div className="mx-auto w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="h-7 w-7 text-green-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">¡Correo Enviado!</h3>
                  <p className="text-white/50 text-sm mb-6">
                    Si el correo existe en nuestro sistema, recibirás instrucciones para restablecer tu contraseña.
                  </p>
                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotSuccess(false);
                      setForgotEmail('');
                    }}
                    className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl text-white text-sm font-medium hover:from-cyan-400 hover:to-teal-400 transition-all"
                  >
                    Volver al Login
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setShowForgotPassword(false)}
                    className="absolute top-4 right-4 text-white/30 hover:text-white/60 transition-colors"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  
                  <div className="text-center mb-6">
                    <div className="mx-auto w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center mb-4">
                      <Mail className="h-6 w-6 text-cyan-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-1">Recuperar Contraseña</h3>
                    <p className="text-white/40 text-sm">
                      Ingresa tu correo electrónico registrado
                    </p>
                  </div>
                  
                  <form onSubmit={handleForgotPassword}>
                    <div className="relative mb-4">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        placeholder="correo@ejemplo.com"
                        className="w-full h-11 pl-10 pr-4 bg-white/[0.05] border border-white/[0.08] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-cyan-400/40 transition-all"
                        data-testid="forgot-email-input"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full h-11 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl text-white text-sm font-medium hover:from-cyan-400 hover:to-teal-400 transition-all disabled:opacity-50"
                      data-testid="forgot-submit-button"
                    >
                      {forgotLoading ? 'Enviando...' : 'Enviar Instrucciones'}
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      className="w-full mt-3 text-xs text-white/40 hover:text-white/60 transition-colors"
                    >
                      Cancelar
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
